import { useState, useEffect } from "react";
import { InitiativeData, InteractionData, OutcomeData, TargetsData } from "../services/types";
import { getFiscalYearStartMonth, sanitizeString } from "../utils/helpers";
import { useYearTypeContext } from "../context/YearTypeContext";

interface Totals {
    bre: number;
    recruitment: number;
    training: number;
    startup: number;
    export: number;
    marketing_plan_required: number;
    marketing_plan_received: number;
}

const useFilteredData = (
    targets?: TargetsData[],
    outcomes?: OutcomeData[],
    initiatives?: InitiativeData[],
    interactions?: InteractionData[],
    searchTerm: string = '',
    sortType: string = 'alphabetical',
    year: string = '',
    quarter: string = '',
    ado: string = '',
    interactionType: string = '',
    selectedInitiative: string = '',
    assistanceType: string = ''
) => {
    const { yearType } = useYearTypeContext();
    const [filteredTargets, setFilteredTargets] = useState<TargetsData[]>([]);
    const [filteredInitiatives, setFilteredInitiatives] = useState<InitiativeData[]>([]);
    const [filteredOutcomes, setFilteredOutcomes] = useState<OutcomeData[]>([]);
    const [filteredInteractions, setFilteredInteractions] = useState<InteractionData[]>([]);
    const [totalTargets, setTotalTargets] = useState<Totals>({
        bre: 0,
        recruitment: 0,
        training: 0,
        startup: 0,
        export: 0,
        marketing_plan_required: 0,
        marketing_plan_received: 0
    });
    
    useEffect(() => {

        ///////////////
        // TARGET FILTERS:
        // Filter and calculate totals for targetsData
        ///////////////
        let filteredTargetsResult = targets ? [...targets] : [];

        if (searchTerm) {
            filteredTargetsResult = filteredTargetsResult.filter(target => {
                const targetAdo = target.partner_name ? sanitizeString(target.partner_name) : '';
                
                return targetAdo.includes(sanitizeString(searchTerm));
            });
        }

        if (ado) {
            const sanitizedAdo = sanitizeString(ado);
        
            if (sanitizedAdo !== 'admin' && sanitizeString(ado) !== 'cai-admin') {
                // Filter by partner_name if ado is not 'admin'
                filteredTargetsResult = filteredTargetsResult.filter(item => {
                    const targetAdo = item.partner_name ? sanitizeString(item.partner_name) : '';
                    return targetAdo === sanitizedAdo;
                });
            }
            // If ado is 'admin', do nothing; all targets are included
        }

        if (year) {
            filteredTargetsResult = filteredTargetsResult.filter(item =>
                item.year === year
            );
        }

        if (sortType === 'date') {
            filteredTargetsResult.sort((a, b) => b.id - a.id);
        } else {
            filteredTargetsResult.sort((a, b) => a.partner_name.localeCompare(b.partner_name));
        }

        const totalsTargets = filteredTargetsResult.reduce((acc, item) => {
            acc.bre += item.bre || 0;
            acc.recruitment += item.recruitment || 0;
            acc.training += item.training || 0;
            acc.startup += item.startup || 0;
            acc.export += item.export || 0;

            if (item.marketing_plan_required === true) {
                acc.marketing_plan_required += 1;
            }
            if (item.marketing_plan_received === true) {
                acc.marketing_plan_received += 1;
            }
            return acc;
        }, {
            bre: 0,
            recruitment: 0,
            training: 0,
            startup: 0,
            export: 0,
            marketing_plan_required: 0,
            marketing_plan_received: 0
        });

        setFilteredTargets(filteredTargetsResult);
        setTotalTargets(totalsTargets);

        ///////////////
        // INITIATIVE FILTERS:
        // Filter and calculate totals for initiatives
        ///////////////
        let filteredInitiativesResult = initiatives ? [...initiatives] : [];

        if (ado && initiatives) {
            if (sanitizeString(ado) !== 'admin' && sanitizeString(ado) !== 'cai-admin') {
                // Filter by partner_name if ado is not 'Admin'
                filteredInitiativesResult = filteredInitiativesResult.filter(item => {
                    const initiativeAdo = item.partner_name ? sanitizeString(item.partner_name) : '';
                    return initiativeAdo === sanitizeString(ado);
                });
            }
            // If ado is 'Admin', do nothing; all initiatives are included
        }

        if (searchTerm) {
            filteredInitiativesResult = filteredInitiativesResult.filter(initiative => {
                const initiativeName = initiative.name ? sanitizeString(initiative.name) : '';
                
                return initiativeName.includes(sanitizeString(searchTerm));
            });
        }

        if (sortType === 'date') {
            filteredInitiativesResult.sort((a, b) => b.id - a.id);
        } else {
            filteredInitiativesResult.sort((a, b) => {
                const nameA = a.name || ''; 
                const nameB = b.name || ''; 
                return nameA.localeCompare(nameB);
            });
        } 

        if (year) {
            filteredInitiativesResult = filteredInitiativesResult.filter(item =>
                item.year === year
            );
        }

        if (quarter) {
            filteredInitiativesResult = filteredInitiativesResult.filter(item => item.quarter === quarter);
        }

        if (selectedInitiative) {
            filteredInitiativesResult = filteredInitiativesResult.filter(item => Number(item.id) === Number(selectedInitiative));
        }


        console.log('filteredInitiativesResult:', filteredInitiativesResult);
        setFilteredInitiatives(filteredInitiativesResult);

        ///////////////
        // OUTCOME FILTERS:
        // Filter and calculate totals for outcomes
        ///////////////
        let filteredOutcomesResult = outcomes ? [...outcomes] : [];

        if (ado && initiatives) {
            if (sanitizeString(ado) !== 'admin' && sanitizeString(ado) !== 'cai-admin') {
                // Find initiative_id corresponding to the selected ado
                const initiativeIds = initiatives
                    .filter(init => sanitizeString(init.partner_name) === sanitizeString(ado))
                    .map(init => init.id);
        
                filteredOutcomesResult = filteredOutcomesResult
                    .filter(item => initiativeIds.includes(Number(item.initiative_id)));
            }
            // If ado is 'Admin', do nothing; all outcomes are included
        }

        if (year) {
            filteredOutcomesResult = filteredOutcomesResult.filter(item =>
                item.year === year
            );
        }

        if (quarter) {
            filteredOutcomesResult = filteredOutcomesResult.filter(item => item.quarter === quarter);
        }

        if (searchTerm && initiatives) {
            filteredOutcomesResult = filteredOutcomesResult.filter(outcome => {
                const initiative = initiatives.find(init => init.id === Number(outcome.initiative_id));
                const adoName = initiative ? initiative.partner_name : '';
                const initiativeName = initiative ? sanitizeString(initiative.name) : '';
                
                return (
                    adoName.toLowerCase().includes(sanitizeString(searchTerm)) ||
                    initiativeName.includes(sanitizeString(searchTerm))
                );
            });
        }

        if (selectedInitiative) {
            filteredOutcomesResult = filteredOutcomesResult.filter(item => Number(item.initiative_id) === Number(selectedInitiative));
        }

        console.log('filteredOutcomesResult:', filteredOutcomesResult);
        setFilteredOutcomes(filteredOutcomesResult);

        ///////////////
        // INTERACTION FILTERS:
        // Filter and calculate totals for interactions data
        ///////////////
        let filteredInteractionsResult = interactions ? [...interactions] : [];

        if (searchTerm && initiatives) {
            filteredInteractionsResult = filteredInteractionsResult.filter(interaction => {
                const initiative = initiatives.find(init => init.id === interaction.initiative_id);
                const businessName = interaction ? interaction.business_trade_name : '';
                const initiativeName = initiative ? sanitizeString(initiative.name) : '';
                
                return (
                    businessName?.toLowerCase().includes(sanitizeString(searchTerm)) ||
                    initiativeName?.includes(sanitizeString(searchTerm))
                );
            });
        }

        if (ado && initiatives) {
            if (sanitizeString(ado) !== 'admin' && sanitizeString(ado) !== 'cai-admin') {
                // Find initiative_id corresponding to the selected ado
                const initiativeIds = initiatives
                    .filter(init => sanitizeString(init.partner_name) === sanitizeString(ado))
                    .map(init => init.id.toString());
        
                filteredInteractionsResult = filteredInteractionsResult
                    .filter(item => initiativeIds.includes(item.initiative_id.toString()));
            }
            // If ado is 'Admin', do nothing; all interactions are included
        }

        if (year) {
            // Convert the year to a number for comparison
            const selectedYear = Number(year);
            
            const fiscalYearStartMonth = getFiscalYearStartMonth(yearType);

            let rangeStart: Date;
            let rangeEnd: Date;

            // Fiscal year starts at fiscalYearStartMonth of the *previous* year
            // and ends the day before the start of the fiscalYearStartMonth of the selected year
            rangeStart = new Date(selectedYear - 1, fiscalYearStartMonth - 1, 1); // e.g., Jul 1 of previous year

            // To get end of fiscal year: month before start month of selected year
            const rangeEndMonth = fiscalYearStartMonth - 1 === 0 ? 11 : fiscalYearStartMonth - 2;
            const rangeEndYear = fiscalYearStartMonth - 1 === 0 ? selectedYear : selectedYear;
            rangeEnd = new Date(rangeEndYear, rangeEndMonth + 1, 0, 23, 59, 59, 999); // last day of prior month

            filteredInteractionsResult = filteredInteractionsResult.filter(item => {
                const interactionDate = new Date(item.interaction_date);
                return interactionDate >= rangeStart && interactionDate <= rangeEnd;
            });
        }

        if (selectedInitiative) {
            filteredInteractionsResult = filteredInteractionsResult.filter(item => item.initiative_id == Number(selectedInitiative));
        }

        if (interactionType) {
            filteredInteractionsResult = filteredInteractionsResult.filter(item => item.interaction_type?.id === Number(interactionType));
        }

        if (assistanceType) {
            console.log('assistance type:', assistanceType);
            filteredInteractionsResult = filteredInteractionsResult.filter(item =>
                item.assistance_types.some(assistance => assistance.id === assistanceType)
            );
        }

        console.log('filteredInteractionsResult:', filteredInteractionsResult);
        setFilteredInteractions(filteredInteractionsResult);

    }, [targets, outcomes, initiatives, interactions, searchTerm, sortType, year, quarter, ado, interactionType, selectedInitiative, assistanceType, yearType]);

    return { filteredTargets, totalTargets, filteredInitiatives, filteredOutcomes, filteredInteractions };
};

export default useFilteredData;
